import React, { useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { Query } from "lib/Query";
import LoadingSpinner from "components/Lottie/loading";
import ServerError from "components/ServerError";
import useCustomerUser from "hooks/use-customer-user";
import useVendorUser from "hooks/use-vendor-user";
import { trackResourceChange } from "lib/data-layer-push";
import { TestContext } from "lib/test-utils";
import dynamic from "next/dynamic";

function PageQuery({
  ns,
  userType,
  readMethod,
  query,
  suppressLoader,
  onReadLoad,
  onSubmitError,
  onSubmitSuccess,
  children,
}) {
  const customerUser = useCustomerUser();
  const vendorUser = useVendorUser();

  const ctx = useContext(TestContext);
  if (process.env.NODE_ENV === "test" && ctx) {
    return children(ctx);
  }

  const currentUser = useMemo(() => { // eslint-disable-line
    if (customerUser) {
      return {
        accountType: "customer",
        userID: customerUser.id,
      };
    }

    if (vendorUser) {
      return {
        accountType: "provider",
        userID: vendorUser.id,
      };
    }

    return {
      accountType: "none",
      userID: undefined,
    };
  }, [customerUser, vendorUser]);

  return (
    <Query
      ns={ns}
      onSubmitError={onSubmitError}
      onSubmitSuccess={onSubmitSuccess}
      userType={userType}
      query={query}
      readMethod={readMethod}
      onReadLoad={onReadLoad}
      onCreate={(data) => {
        trackResourceChange({ data, currentUser, eventType: "add" });
      }}
      onUpdate={(data) => {
        trackResourceChange({ data, currentUser, eventType: "edit" });
      }}
      onDestroy={(data) =>
        trackResourceChange({ data, currentUser, eventType: "delete" })
      }
      render={{
        Loading: () => (suppressLoader ? <></> : <LoadingSpinner />),
        Error: () => {
          return <ServerError />;
        },
        Success: (props) => {
          return children(props);
        },
      }}
    />
  );
}

PageQuery.propTypes = {
  ns: PropTypes.string,
  onSubmitError: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
  suppressLoader: PropTypes.bool,
  query: PropTypes.object.isRequired,
  userType: PropTypes.string,
  children: PropTypes.func.isRequired,
  readMethod: PropTypes.string,
  onReadLoad: PropTypes.func,
};

PageQuery.defaultProps = {
  ns: undefined,
  onSubmitError: undefined,
  onSubmitSuccess: undefined,
  userType: "customer",
  onReadLoad: undefined,
  suppressLoader: false,
  readMethod: "get",
};


const dynamicPromise = typeof dynamic === 'function' ?
  dynamic(() => Promise.resolve(PageQuery), {
    ssr: false,
  }) :
  PageQuery;

export default dynamicPromise;
